import { Button, Input, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CLAIM_STATUS_CATALOG_DEFAULT, type ClaimStatusCatalog } from '@warehouse/shared';
import { RotateCcw, Save } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastSuccess } from '../../../lib/notify';
import { claimKeys, fetchClaimStatuses, saveClaimStatuses } from '../api/claims-api';

/**
 * `/orders/claims/statuses` — the editable status catalog.
 *
 * The old WMS had a `complaint_statuses` TABLE (name + colour + sort order);
 * the Wave-1 schema models the status itself as a fixed enum, so the editable
 * half lives in `app_settings`. Keys are therefore read-only here, and the
 * labels are per language because a Chinese-speaking restaurant should not have
 * to read "Under behandling".
 */
export function ClaimStatusesPage(): ReactNode {
  const { t } = useTranslation(['claims', 'common']);
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: claimKeys.statuses(), queryFn: fetchClaimStatuses });

  const [draft, setDraft] = useState<ClaimStatusCatalog>(CLAIM_STATUS_CATALOG_DEFAULT);
  useEffect(() => {
    if (query.data) setDraft(query.data.statuses);
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => saveClaimStatuses(draft),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: claimKeys.all });
      toastSuccess(t('claims:statusCatalog.saved'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  function patch(index: number, changes: Partial<ClaimStatusCatalog[number]>): void {
    setDraft((current) =>
      current.map((entry, i) => (i === index ? { ...entry, ...changes } : entry)),
    );
  }

  return (
    <div>
      <PageHeader
        title={t('claims:statusCatalog.title')}
        subtitle={t('claims:statusCatalog.subtitle')}
        actions={
          <>
            <Button
              type="button"
              variant="secondary"
              icon={<RotateCcw />}
              onClick={() => {
                setDraft(query.data?.defaults ?? CLAIM_STATUS_CATALOG_DEFAULT);
              }}
            >
              {t('claims:statusCatalog.reset')}
            </Button>
            <Button
              type="button"
              icon={<Save />}
              loading={save.isPending}
              onClick={() => {
                save.mutate();
              }}
            >
              {t('common:actions.save')}
            </Button>
          </>
        }
      />

      <Surface variant="card" className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted">
              <th className="py-2 pr-3">{t('claims:statusCatalog.key')}</th>
              <th className="py-2 pr-3">{t('claims:statusCatalog.labelSv')}</th>
              <th className="py-2 pr-3">{t('claims:statusCatalog.labelEn')}</th>
              <th className="py-2 pr-3">{t('claims:statusCatalog.labelZh')}</th>
              <th className="py-2 pr-3">{t('claims:statusCatalog.color')}</th>
              <th className="py-2 pr-3 text-right">{t('claims:statusCatalog.sortOrder')}</th>
              <th className="py-2 text-center">{t('claims:statusCatalog.active')}</th>
            </tr>
          </thead>
          <tbody>
            {draft.map((entry, index) => (
              <tr key={entry.key} className="border-b border-surface-border/60">
                <td className="py-2 pr-3">
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-xs"
                    style={{ backgroundColor: `${entry.color}1f`, color: entry.color }}
                  >
                    {entry.key}
                  </span>
                </td>
                {(['sv', 'en', 'zh'] as const).map((locale) => (
                  <td key={locale} className="py-2 pr-3">
                    <Input
                      aria-label={`${entry.key} ${locale}`}
                      value={entry.labels[locale]}
                      onChange={(event) => {
                        patch(index, { labels: { ...entry.labels, [locale]: event.target.value } });
                      }}
                    />
                  </td>
                ))}
                <td className="py-2 pr-3">
                  <input
                    type="color"
                    aria-label={`${entry.key} ${t('claims:statusCatalog.color')}`}
                    className="h-8 w-12 rounded border border-surface-border bg-surface"
                    value={entry.color}
                    onChange={(event) => {
                      patch(index, { color: event.target.value });
                    }}
                  />
                </td>
                <td className="py-2 pr-3 text-right">
                  <input
                    type="number"
                    min={0}
                    max={999}
                    aria-label={`${entry.key} ${t('claims:statusCatalog.sortOrder')}`}
                    className="w-16 rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-right text-sm"
                    value={entry.sortOrder}
                    onChange={(event) => {
                      patch(index, { sortOrder: Number(event.target.value) });
                    }}
                  />
                </td>
                <td className="py-2 text-center">
                  <input
                    type="checkbox"
                    aria-label={`${entry.key} ${t('claims:statusCatalog.active')}`}
                    checked={entry.active}
                    onChange={(event) => {
                      patch(index, { active: event.target.checked });
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Surface>
    </div>
  );
}
